import type { DynamicIslandActivity } from '../../../context/dynamicIslandTypes'
import { ChromeToolbarButton, ChromeToolbarDangerButton } from '../../ChromeActions'
import { ThemeDynamicIslandActionRow } from './ThemeDynamicIsland'
import { activityIslandAccent, activityPreviewProgress } from '../../../lib/dynamicIsland/categoryOrder'
import {
  dynamicIslandAccentClass,
  dynamicIslandProgressFillClass,
  dynamicIslandProgressTrackClass,
  dynamicIslandSourceTagClass,
  dynamicIslandStatusChipClass,
} from '../../../lib/dynamicIsland/dynamicIslandPresentation'
import {
  themeDynamicIslandActionButtonClass,
  themeDynamicIslandExpandedBodyClass,
} from './dynamicIslandTokens'

const islandActionBtn = themeDynamicIslandActionButtonClass()

type Props = {
  activity: DynamicIslandActivity
  angular: boolean
  compact?: boolean
  layoutMode?: 'solo' | 'multi'
}

export function DynamicIslandExpandedSummary({
  activity,
  angular,
  compact = false,
  layoutMode = 'multi',
}: Props) {
  const { expanded, sourceLabel, lifecycle } = activity
  const progressTrack = dynamicIslandProgressTrackClass(angular)
  const progressPercent = activityPreviewProgress(activity)
  const accent = activityIslandAccent(activity)
  const workflowSoloInset = compact && layoutMode === 'solo' && activity.sourceKind === 'workflow.run'
  const progressFill = dynamicIslandProgressFillClass({
    accent,
    progressPercent,
    lifecycle,
  })
  const showProgress = progressPercent > 0 || lifecycle === 'active'

  return (
    <div
      className={[
        themeDynamicIslandExpandedBodyClass(),
        compact && layoutMode === 'multi' ? 'glyphicai-dynamic-island-expanded--multi' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="region"
      aria-label={expanded.title}
    >
      <div
        className={[
          'glyphicai-dynamic-island-expanded-summary__head',
          compact ? 'glyphicai-dynamic-island-expanded-summary__head--multi' : '',
          compact && layoutMode === 'solo' ? 'glyphicai-dynamic-island-expanded-summary__head--solo' : '',
        ].join(' ')}
      >
        {!compact ? (
          <span
            className={[
              'glyphicai-dynamic-island-activity__accent',
              dynamicIslandAccentClass(accent),
            ].join(' ')}
            aria-hidden
          />
        ) : null}
        <div
          className={[
            'min-w-0 flex flex-1 flex-col justify-center',
            workflowSoloInset ? 'glyphicai-dynamic-island-expanded-summary__content--workflow-solo' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {!compact ? (
            <div className="flex min-w-0 items-center gap-1.5">
              <span className={dynamicIslandSourceTagClass()}>{sourceLabel}</span>
              {lifecycle === 'active' && expanded.statusLabel ? (
                <span className={dynamicIslandStatusChipClass(accent)}>{expanded.statusLabel}</span>
              ) : null}
            </div>
          ) : null}
          <p
            className={[
              'truncate text-xs font-semibold leading-snug text-ink',
              compact ? '' : 'mt-0.5',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {expanded.title}
          </p>
          <p className="mt-0.5 truncate text-[11px] leading-snug text-ink-soft">{expanded.description}</p>
          {showProgress ? (
            <div className={['mt-1.5', progressTrack].join(' ')}>
              <span
                className={['glyphicai-dynamic-island-activity__progress-fill', progressFill].join(' ')}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <ThemeDynamicIslandActionRow className="justify-center gap-1.5 sm:justify-end">
        {activity.actions.map((action) => {
          const Btn = action.variant === 'danger' ? ChromeToolbarDangerButton : ChromeToolbarButton
          return (
            <Btn
              key={action.id}
              type="button"
              className={islandActionBtn}
              disabled={action.disabled}
              onClick={action.onClick}
            >
              {action.label}
            </Btn>
          )
        })}
      </ThemeDynamicIslandActionRow>
    </div>
  )
}
